import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

import { useUpdateClientStatusMutation } from "Clients/Api/Mutations/useUpdateClientStatusMutation";
import { useClientDetailQuery } from "Clients/Api/Queries/useClientDetailQuery";
import { ClientDocumentsSection } from "Clients/Components/ClientDocumentsSection";
import { ClientInformation } from "Clients/Components/ClientInformation";
import { EditClientModal } from "Clients/Components/EditClientModal";
import { ClientStatus } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  formatStreetAddress,
  translateCountryCode,
} from "Infrastructure/Utils/AddressUtils";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { ProjectListSection } from "Projects/Components/ProjectListSection";
import { BackButton } from "Shared/Buttons/BackButton";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";

export const ClientDetailPage = (): React.ReactNode => {
  const { t } = useResource();
  const { clientId } = useParams<{ clientId: string }>();
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);

  const { data: client, isLoading } = useClientDetailQuery(clientId);
  const updateClientStatusMutation = useUpdateClientStatusMutation();

  const handleStatusChange = async () => {
    if (!clientId || !client) return;

    const newStatus =
      client.status === ClientStatus.Active
        ? ClientStatus.Inactive
        : ClientStatus.Active;

    try {
      await updateClientStatusMutation.mutateAsync({
        clientID: clientId,
        status: newStatus,
      });

      toast.success(
        newStatus === ClientStatus.Active
          ? t(Resources.Clients.ClientDetail.ActivateSuccess)
          : t(Resources.Clients.ClientDetail.DeactivateSuccess),
      );
    } catch {
      toast.error(t(Resources.Clients.ClientDetail.StatusChangeError));
    }
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.Clients)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  if (!client) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.Clients)}>
        <Typography>{t(Resources.Clients.ClientDetail.NotFound)}</Typography>
      </AuthenticatedLayout>
    );
  }

  const clientProjects = client.projects ?? [];

  return (
    <AuthenticatedLayout
      preTitle={
        <BackButton
          defaultText={t(Resources.Common.BackToClients)}
          defaultPath={AppRouting.Clients.path()}
        />
      }
      title={client.companyName}
      action={
        <Button
          variant="contained"
          color={client.status === ClientStatus.Active ? "error" : "success"}
          onClick={handleStatusChange}
          disabled={updateClientStatusMutation.isPending}
        >
          {client.status === ClientStatus.Active
            ? t(Resources.Clients.ClientDetail.Deactivate)
            : t(Resources.Clients.ClientDetail.Activate)}
        </Button>
      }
    >
      <Grid container columnSpacing={Spacing.L} rowSpacing={Spacing.M}>
        <Grid size={6}>
          <ClientInformation
            companyNumber={client.companyNumber}
            taxNumber={client.taxNumber}
            email={client.email ?? ""}
            phone={client.phone ?? ""}
            address={formatStreetAddress(
              client.address?.street,
              client.address?.streetNumber,
              client.address?.orientationNumber,
            )}
            city={client.address?.municipality ?? undefined}
            postalCode={client.address?.postalCode ?? undefined}
            state={translateCountryCode(client.address?.countryCode, t)}
            onEdit={() => setIsEditClientModalOpen(true)}
          />
        </Grid>
        <Grid size={6}>
          <Stack spacing={Spacing.M}>
            <ProjectListSection
              title={t(Resources.Clients.ClientDetail.Projects)}
              emptyMessage={t(Resources.Clients.ClientDetail.NoProjects)}
              projects={clientProjects}
            />
          </Stack>
        </Grid>
      </Grid>
      <Box sx={{ mt: Spacing.M }}>
        <ClientDocumentsSection clientID={clientId!} />
      </Box>
      <EditClientModal
        isOpen={isEditClientModalOpen}
        onClose={() => setIsEditClientModalOpen(false)}
        client={client}
        clientID={clientId!}
      />
    </AuthenticatedLayout>
  );
};
